import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

import { EditableListDropdownItem } from './editable-list-dropdown-item.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, NgClass, IconComponent, FormsModule, TooltipDirective],
  selector: 'aux-editable-list-dropdown',
  styleUrls: ['editable-list-dropdown.component.scss'],
  templateUrl: './editable-list-dropdown.component.html',
})
export class EditableListDropdownComponent implements OnInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  createDisabled = input(false);
  createDisabledTooltip = input('');
  deleteDisabled = input(false);
  deleteDisabledTooltip = input('');
  editDisabled = input(false);
  editDisabledTooltip = input('');

  @Input() classList = '';
  @Output() create = new EventEmitter();
  @Input() createBtnLabel = '';
  @Output() delete = new EventEmitter<EditableListDropdownItem>();
  @Input() dropdownFormControl = new FormControl('');
  @Output() edit = new EventEmitter<EditableListDropdownItem>();
  formControlValue: null | string = '';
  @Input() id = '';
  @Input() items: EditableListDropdownItem[] = [];
  @Input() label = '';

  ngOnInit(): void {
    this.dropdownFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.formControlValue = value || null;
        this.changeDetectorRef.detectChanges();
      });
  }

  getLabelText(value: string): string {
    return this.items.find((item) => item.value === value)?.name || '';
  }

  onCreate(): void {
    if (this.createDisabled()) {
      return;
    }

    this.create.emit();
  }

  onDelete(event: Event, item: EditableListDropdownItem): void {
    event.stopPropagation();

    if (item.disabled || this.deleteDisabled()) {
      return;
    }

    this.delete.emit(item);
  }

  onEdit(event: Event, item: EditableListDropdownItem): void {
    event.stopPropagation();

    if (item.disabled || this.editDisabled()) {
      return;
    }

    this.edit.emit(item);
  }

  select(value: string): void {
    this.dropdownFormControl.setValue(value);
  }
}
